import type { Departamento, FieldValue, FormField, LocationValue } from './types';

// Client-side mirror of fields.py `validate_inputs` / `_coerce` (CLIENT side).
// The server re-validates everything and answers 422 with the same messages,
// so keep the wording and limits in sync by hand.

export const TEXT_MAX = 500;
export const TEXTAREA_MAX = 2000;

// What the inputs hold while the person types (numbers stay strings until submit).
export type RawValue = string | boolean | LocationValue;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
// What Python's float() accepts for plain decimals (no exponent, hex, inf or nan).
const NUMBER_RE = /^[+-]?(\d+\.?\d*|\.\d+)$/;

export function initialValue(field: FormField): RawValue {
  if (field.type === 'boolean') return false;
  if (field.type === 'location') return { departamentoCode: '', municipioCode: '' };
  return '';
}

function isEmpty(field: FormField, raw: RawValue): boolean {
  if (field.type === 'boolean') return false; // always sent as true/false
  if (field.type === 'location') {
    const loc = raw as LocationValue;
    return !loc.departamentoCode && !loc.municipioCode;
  }
  return typeof raw !== 'string' || !raw.trim();
}

// Guatemala writes 1,234.56 — commas are thousands separators.
export function parseNumber(raw: string): number | null {
  const s = raw.trim().replace(/,/g, '').replace(/\s/g, '');
  if (!NUMBER_RE.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function isRealDate(s: string): boolean {
  if (!DATE_RE.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/** Returns [clean value to send, error message]. Both null = empty and optional (omitted). */
export function validateField(
  field: FormField,
  raw: RawValue,
  locations: Departamento[] | null,
): [FieldValue | null, string | null] {
  if (isEmpty(field, raw)) {
    return [null, field.required ? 'obligatorio' : null];
  }
  switch (field.type) {
    case 'text':
    case 'textarea': {
      const v = (raw as string).trim();
      const limit = field.type === 'text' ? TEXT_MAX : TEXTAREA_MAX;
      if (v.length > limit) return [null, `máximo ${limit} caracteres`];
      return [v, null];
    }
    case 'number':
    case 'money':
    case 'percent': {
      let n = parseNumber(raw as string);
      if (n === null) return [null, 'debe ser un número'];
      if (field.type !== 'number' && n < 0) return [null, 'no puede ser negativo'];
      if (field.type === 'percent' && n > 100) return [null, 'no puede ser mayor a 100'];
      if (field.type === 'money') n = Math.round(n * 100) / 100;
      return [n, null];
    }
    case 'boolean':
      return [raw === true, null];
    case 'select': {
      const allowed = (field.options ?? []).map((o) => o.value);
      if (!allowed.includes(raw as string)) return [null, 'opción no válida'];
      return [raw as string, null];
    }
    case 'date': {
      const v = (raw as string).trim();
      if (!isRealDate(v)) return [null, 'fecha inválida (AAAA-MM-DD)'];
      return [v, null];
    }
    case 'location': {
      const { departamentoCode: dc, municipioCode: mc } = raw as LocationValue;
      if (!dc || !mc) return [null, 'seleccione departamento y municipio'];
      const dep = locations?.find((d) => d.code === dc);
      if (!dep || !mc.startsWith(dc) || !dep.municipios.some((m) => m.code === mc)) {
        return [null, 'departamento o municipio no válido'];
      }
      return [{ departamentoCode: dc, municipioCode: mc }, null];
    }
    default:
      return [null, 'tipo no soportado'];
  }
}

/** Validates the whole form: the body for POST /inputs plus per-field errors. */
export function validateForm(
  fields: FormField[],
  raw: Record<string, RawValue>,
  locations: Departamento[] | null,
): { values: Record<string, FieldValue>; errors: Record<string, string> } {
  const values: Record<string, FieldValue> = {};
  const errors: Record<string, string> = {};
  for (const f of fields) {
    const [v, err] = validateField(f, raw[f.key] ?? initialValue(f), locations);
    if (err) errors[f.key] = err;
    else if (v !== null) values[f.key] = v;
  }
  return { values, errors };
}

/** Server and client messages are lowercase fragments ("obligatorio") — show them as sentences. */
export function sentence(msg: string): string {
  const s = msg.trim();
  if (s === 'obligatorio') return 'Este campo es obligatorio.';
  if (!s) return s;
  const cap = s.charAt(0).toUpperCase() + s.slice(1);
  return /[.!?]$/.test(cap) ? cap : `${cap}.`;
}
